import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { React } from 'Imports';
import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import { EllipsisItem, EllipsisPopperMenu } from '$Components/Shared/EllipsisPopperMenu';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { useState } from 'react';

interface IVideoRecallListRowActionsProps {
    id: number;
    promoteVideo: (eventId: number) => void;
    deleteVideoRecall?: (eventId: number) => void;
    workflowStep?: string;
    isEncompass?: boolean;
}

export function VideoRecallListRowActions(props: IVideoRecallListRowActionsProps) {
    const { workflowStep, id, isEncompass } = props;
    const { showSnackbarMessage } = useAppSnackbar();
    const [showPromotePopup, setShowPromotePopup] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [hasPromoted, setHasPromoted] = useState(false);
    const [hasDeleted, setHasDeleted] = useState(false);

    const statusSuccess = workflowStep == 'Success';
    const canPromote = canEditByRole([RolesEnum.videoRecallEdit, RolesEnum.encompassVideo]);
    const canDelete = id > 0 && canEditByRole([RolesEnum.videoAdmin]);

    const togglePromotePopup = () => setShowPromotePopup((prev) => !prev);
    const toggleDeletePopup = () => setShowDeletePopup((prev) => !prev);

    const handlePromoteSubmit = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await props.promoteVideo(id);
            setHasPromoted(true);
            showSnackbarMessage({
                message: 'Video recall promoted successfully.',
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
        } catch {
            showSnackbarMessage({
                message: 'Unable to promote video recall.',
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
        }
        setShowPromotePopup(false);
    };

    const handleDeleteSubmit = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            if (props.deleteVideoRecall) {
                await props.deleteVideoRecall(id);
            }
            setHasDeleted(true);
            showSnackbarMessage({
                message: 'Video recall deleted successfully.',
                alertType: AlertTypes.Success,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
        } catch {
            showSnackbarMessage({
                message: 'Unable to delete video recall.',
                alertType: AlertTypes.Fail,
                isEncompass: isEncompass,
                showIcon: isEncompass,
            });
        }
        setShowDeletePopup(false);
    };

    const items: EllipsisItem[] = [
        {
            key: 'promote',
            text: 'Promote',
            isHidden: !canPromote,
            isDisabled: !statusSuccess || hasPromoted,
            onClick: togglePromotePopup,
        },
        {
            key: 'delete',
            text: 'Delete',
            isHidden: !canDelete,
            isDisabled: hasDeleted,
            onClick: toggleDeletePopup,
        },
    ];

    return (
        <>
            {items.some((item) => !item.isHidden) && <EllipsisPopperMenu items={items} isEncompass={isEncompass} />}
            <ConfirmDialog
                isOpen={showPromotePopup}
                title={isEncompass ? 'Promote Video Recall' : 'Confirm Promote'}
                contentMessage="Are you sure you want to promote this video recall?"
                acceptButton="Promote"
                cancelButton="Cancel"
                isButtonAlert={false}
                handleModalClosed={togglePromotePopup}
                handleSubmit={handlePromoteSubmit}
            />
            <ConfirmDialog
                isOpen={showDeletePopup}
                title={isEncompass ? 'Delete Video Recall' : 'Confirm Delete'}
                contentMessage="Are you sure you want to delete this video recall?"
                acceptButton="Delete"
                cancelButton="Cancel"
                isAlert={isEncompass}
                alertType={AlertTypes.Alert}
                showIcon={isEncompass}
                isButtonAlert
                contentMessageMarginTop={isEncompass ? '0' : undefined}
                handleModalClosed={toggleDeletePopup}
                handleSubmit={handleDeleteSubmit}
            />
        </>
    );
}
